import React, {useState, useEffect} from 'react'
import {useCurrentBasket} from '../../hooks/basket/useCurrentBasket'
import {localStorageAction} from '../../utils/common'
import {Input} from '../ui/input'
import {Button} from '../ui/button'

interface InteractiveSpendingToolProps {
    disclaimerText?: string
    dataTestid?: string
    defaultSpendingAmount?: number
    remainingText?: string
    ctaText?: string
    amountInputText?: string
    onSubmit?: (amount: number) => void
}

export const InteractiveSpendingTool: React.FC<InteractiveSpendingToolProps> = ({
    disclaimerText,
    defaultSpendingAmount,
    dataTestid,
    remainingText,
    ctaText,
    amountInputText,
    onSubmit
}) => {
    const {data: basket} = useCurrentBasket()
    const defaultAmount =
        defaultSpendingAmount !== undefined && defaultSpendingAmount !== null
            ? defaultSpendingAmount
            : 500
    const [inputValue, setInputValue] = useState(defaultAmount)
    const fsaFunds = Math.trunc(inputValue) || 0
    const cartTotal = basket?.productTotal || 0

    const remaining = Math.max(0, fsaFunds - cartTotal)
    const percentageUsed = Math.min(100, Math.max(0, (cartTotal / fsaFunds) * 100))
    const exceededAmount = remaining == 0

    const handleSubmit = () => {
        const amount = inputValue
        if (!isNaN(amount)) {
            onSubmit?.(amount)
            localStorageAction('set', 'interactive-spending-tool', amount.toString())
        }
    }

    const checkForLocalStorage = () => {
        const localStorageKey = 'interactive-spending-tool'
        const localStorageGet = localStorageAction('get', localStorageKey)
        if (localStorageGet !== null && localStorageGet !== undefined) {
            setInputValue(parseInt(localStorageGet))
        }
    }

    useEffect(() => {
        checkForLocalStorage()
    }, [])

    return (
        <div className="px-12 sm:px-16">
            <div className="flex flex-col sm:flex-row items-center gap-2 font-medium">
                <label htmlFor="fsa-input" className="text-black">
                    {amountInputText}
                </label>
                <div className="flex items-center gap-1">
                    <span className="text-xl font-medium text-black">$</span>
                    <Input
                        type="number"
                        id="fsa-input"
                        className="w-44 border-2 rounded-md px-2 py-1 text-lg text-black font-medium focus:outline-none"
                        value={inputValue}
                        onChange={(e) => setInputValue(parseInt(e.target.value))}
                        data-testid="fsa-input"
                    />
                    <Button
                        onClick={handleSubmit}
                        className="bg-blue-03 text-white rounded-md p-2 font-medium text-md"
                        data-testid={dataTestid || 'interactiveSpendingTool-Button'}
                    >
                        {ctaText}
                    </Button>
                </div>
            </div>
            <div className="relative mt-4 h-3 rounded-full bg-gray-02">
                <div
                    className={`h-full  rounded-full transition-all duration-300 ${
                        exceededAmount ? 'bg-red-01' : 'bg-blue-04'
                    }`}
                    style={{width: `${percentageUsed}%`}}
                />
                <div className="absolute right-0 top-1/2 -translate-y-[62%] translate-x-1/2 flex flex-col items-center">
                    <span className="text-xs font-semibold">{remainingText}</span>
                    <div className="relative w-16 h-16 flex items-center justify-center rounded-full bg-gray-02 text-white text-sm font-semibold overflow-hidden">
                        {percentageUsed >= 100 && (
                            <div
                                className={`absolute top-0 left-0 h-full w-full transition-all duration-300 ${
                                    exceededAmount ? 'bg-red-01' : 'bg-blue-04'
                                }`}
                            />
                        )}
                        <span className="relative">${remaining}</span>
                    </div>
                </div>
            </div>
            <p className="text-xs text-right mt-1 mr-16 italic">{disclaimerText}</p>
        </div>
    )
}
